import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  LinearProgress,
  CircularProgress,
  IconButton,
  Collapse,
  Tooltip,
  Button,
  Fade,
} from '@mui/material';
import {
  CheckCircleOutline,
  ErrorOutline,
  ExpandMore,
  ExpandLess,
  Refresh,
  OpenInNew,
  RocketLaunch,
  Schedule,
  PlayCircleOutline,
  Cancel,
} from '@mui/icons-material';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface DeploymentStep {
  name: string;
  status: 'queued' | 'in_progress' | 'completed' | string;
  conclusion: string | null;
  number: number;
  started_at: string | null;
  completed_at: string | null;
}

export interface DeploymentJob {
  name: string;
  status: 'queued' | 'in_progress' | 'completed' | string;
  conclusion: string | null;
  started_at: string | null;
  completed_at: string | null;
  steps: DeploymentStep[];
}

export interface WorkflowInfo {
  id: number;
  name: string;
  path: string;
  state: string;
  filename: string;
  last_run?: {
    status: string;
    conclusion: string | null;
    created_at: string;
    html_url: string;
  };
}

export interface DeploymentStatusData {
  type: 'deployment_status' | 'workflow_list';
  repo_owner: string;
  repo_name: string;
  // deployment_status fields
  run_id?: number;
  run_url?: string;
  workflow_name?: string;
  status?: string;
  conclusion?: string | null;
  branch?: string;
  commit_sha?: string;
  commit_message?: string;
  started_at?: string;
  updated_at?: string;
  jobs?: DeploymentJob[];
  error_messages?: string[];
  // workflow_list fields
  workflows?: WorkflowInfo[];
}

interface DeploymentTrackerProps {
  data: DeploymentStatusData;
  onTriggerDeploy?: (
    repoOwner: string,
    repoName: string,
    workflowFile: string,
  ) => void;
  onRefreshStatus?: (
    repoOwner: string,
    repoName: string,
    runId?: number,
  ) => void;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const COLORS = {
  primary: '#F58321',
  success: '#4caf50',
  error: '#f44336',
  warning: '#ff9800',
  info: '#2196f3',
  queued: '#9e9e9e',
  inProgress: '#2196f3',
  bgDark: '#1e1e2f',
  bgCard: '#262640',
  bgStep: '#2d2d4a',
  border: '#3a3a5c',
  textPrimary: '#ffffff',
  textSecondary: '#b0b0cc',
};

const getStatusColor = (
  status: string,
  conclusion?: string | null,
): string => {
  if (conclusion === 'success') return COLORS.success;
  if (conclusion === 'failure') return COLORS.error;
  if (conclusion === 'cancelled') return COLORS.warning;
  if (status === 'in_progress') return COLORS.inProgress;
  if (status === 'queued') return COLORS.queued;
  return COLORS.textSecondary;
};

const getStatusIcon = (status: string, conclusion?: string | null) => {
  if (conclusion === 'success')
    return <CheckCircleOutline sx={{ color: COLORS.success, fontSize: 18 }} />;
  if (conclusion === 'failure')
    return <ErrorOutline sx={{ color: COLORS.error, fontSize: 18 }} />;
  if (conclusion === 'cancelled')
    return <Cancel sx={{ color: COLORS.warning, fontSize: 18 }} />;
  if (status === 'in_progress')
    return <CircularProgress size={16} sx={{ color: COLORS.inProgress }} />;
  if (status === 'queued')
    return <Schedule sx={{ color: COLORS.queued, fontSize: 18 }} />;
  return <Schedule sx={{ color: COLORS.textSecondary, fontSize: 18 }} />;
};

const formatTime = (dateStr: string | null): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDuration = (
  start: string | null,
  end: string | null,
): string => {
  if (!start) return '';
  const startDate = new Date(start);
  const endDate = end ? new Date(end) : new Date();
  const diffMs = endDate.getTime() - startDate.getTime();
  const secs = Math.floor(diffMs / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const remainSecs = secs % 60;
  return `${mins}m ${remainSecs}s`;
};

const getBorderColor = (isFailure: boolean, isSuccess: boolean): string => {
  if (isFailure) return `${COLORS.error}40`;
  if (isSuccess) return `${COLORS.success}40`;
  return COLORS.border;
};

const getHeaderBg = (isFailure: boolean, isSuccess: boolean): string => {
  if (isFailure) return 'rgba(244,67,54,0.06)';
  if (isSuccess) return 'rgba(76,175,80,0.06)';
  return 'rgba(33,150,243,0.06)';
};

const getStatusLabel = (
  isComplete: boolean,
  isRunning: boolean,
  conclusion?: string | null,
): string => {
  if (isComplete) {
    if (conclusion === 'success') return 'Deployed Successfully';
    if (conclusion === 'failure') return 'Deployment Failed';
    return `Deployment ${conclusion}`;
  }
  if (isRunning) return 'Deploying...';
  return 'Queued';
};

const getRunEmoji = (conclusion: string | null): string => {
  if (conclusion === 'success') return '✅';
  if (conclusion === 'failure') return '❌';
  return '🔄';
};

// ─── Workflow List Sub-Component ────────────────────────────────────────────

const WorkflowListView: React.FC<{
  data: DeploymentStatusData;
  onTriggerDeploy?: DeploymentTrackerProps['onTriggerDeploy'];
}> = ({ data, onTriggerDeploy }) => {
  return (
    <Fade in timeout={400}>
      <Paper
        elevation={0}
        sx={{
          background: COLORS.bgDark,
          borderRadius: '16px',
          border: `1px solid ${COLORS.border}`,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: `1px solid ${COLORS.border}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <PlayCircleOutline sx={{ color: COLORS.primary, fontSize: 24 }} />
          <Box>
            <Typography
              sx={{
                color: COLORS.textPrimary,
                fontWeight: 600,
                fontSize: '0.95rem',
              }}
            >
              Available Workflows
            </Typography>
            <Typography
              sx={{ color: COLORS.textSecondary, fontSize: '0.78rem' }}
            >
              {data.repo_owner}/{data.repo_name}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          {data.workflows?.map((wf, idx) => {
            const lastRun = wf.last_run;
            const isActive = wf.state === 'active';

            return (
              <Box
                key={wf.id || idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  mb: 1,
                  borderRadius: '10px',
                  bgcolor: COLORS.bgCard,
                  border: `1px solid ${COLORS.border}`,
                  cursor: onTriggerDeploy ? 'pointer' : 'default',
                  transition: 'all 0.2s',
                  '&:hover': onTriggerDeploy
                    ? {
                        borderColor: `${COLORS.primary}80`,
                        bgcolor: COLORS.bgStep,
                        transform: 'translateY(-1px)',
                      }
                    : {},
                }}
                onClick={() =>
                  onTriggerDeploy &&
                  onTriggerDeploy(data.repo_owner, data.repo_name, wf.filename)
                }
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: isActive ? COLORS.success : COLORS.queued,
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      color: COLORS.textPrimary,
                      fontSize: '0.85rem',
                      fontWeight: 500,
                    }}
                  >
                    {wf.name}
                  </Typography>
                  <Typography
                    sx={{
                      color: COLORS.textSecondary,
                      fontSize: '0.72rem',
                    }}
                  >
                    {wf.filename}
                    {lastRun && (
                      <>
                        {' '}
                        •{' '}
                        {getRunEmoji(lastRun.conclusion)}{' '}
                        Last:{' '}
                        {lastRun.conclusion || lastRun.status}
                      </>
                    )}
                  </Typography>
                </Box>
                {onTriggerDeploy && (
                  <Tooltip title="Trigger this workflow">
                    <RocketLaunch
                      sx={{ color: COLORS.primary, fontSize: 18 }}
                    />
                  </Tooltip>
                )}
              </Box>
            );
          })}

          {(!data.workflows || data.workflows.length === 0) && (
            <Typography
              sx={{
                color: COLORS.textSecondary,
                textAlign: 'center',
                py: 2,
                fontSize: '0.85rem',
              }}
            >
              No workflows found in this repository.
            </Typography>
          )}
        </Box>
      </Paper>
    </Fade>
  );
};

// ─── Deployment Tracker Component ───────────────────────────────────────────

export const DeploymentTracker: React.FC<DeploymentTrackerProps> = ({
  data,
  onTriggerDeploy,
  onRefreshStatus,
}) => {
  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set());

  // Auto-expand first failing job
  useEffect(() => {
    if (data.type === 'deployment_status' && data.jobs) {
      const failedJob = data.jobs.find(j => j.conclusion === 'failure');
      if (failedJob) {
        setExpandedJobs(prev => new Set(prev).add(failedJob.name));
      }
    }
  }, [data]);

  const toggleJob = useCallback((jobName: string) => {
    setExpandedJobs(prev => {
      const next = new Set(prev);
      if (next.has(jobName)) next.delete(jobName);
      else next.add(jobName);
      return next;
    });
  }, []);

  if (data.type === 'workflow_list') {
    return (
      <WorkflowListView
        data={data}
        onTriggerDeploy={onTriggerDeploy}
      />
    );
  }

  // Calculate overall progress
  const totalSteps =
    data.jobs?.reduce((acc, j) => acc + (j.steps?.length || 0), 0) || 0;
  const completedSteps =
    data.jobs?.reduce(
      (acc, j) =>
        acc +
        (j.steps?.filter(s => s.status === 'completed').length || 0),
      0,
    ) || 0;
  const progressPercent =
    totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

  const isComplete = data.status === 'completed';
  const isSuccess = data.conclusion === 'success';
  const isFailure = data.conclusion === 'failure';
  const isRunning = data.status === 'in_progress';

  return (
    <Fade in timeout={400}>
      <Paper
        elevation={0}
        sx={{
          background: COLORS.bgDark,
          borderRadius: '16px',
          border: `1px solid ${getBorderColor(isFailure, isSuccess)}`,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2.5,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderBottom: `1px solid ${COLORS.border}`,
            background: getHeaderBg(isFailure, isSuccess),
          }}
        >
          <RocketLaunch
            sx={{
              color: getStatusColor(data.status || '', data.conclusion),
              fontSize: 24,
            }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                color: COLORS.textPrimary,
                fontWeight: 600,
                fontSize: '0.95rem',
              }}
            >
              {data.workflow_name || 'Deployment'}
            </Typography>
            <Typography
              sx={{ color: COLORS.textSecondary, fontSize: '0.78rem' }}
            >
              {data.repo_owner}/{data.repo_name}
              {data.branch && ` • ${data.branch}`}
              {data.commit_sha && ` • ${data.commit_sha}`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {onRefreshStatus && (
              <Tooltip title="Refresh status">
                <IconButton
                  size="small"
                  onClick={() =>
                    onRefreshStatus(
                      data.repo_owner,
                      data.repo_name,
                      data.run_id,
                    )
                  }
                  sx={{ color: COLORS.textSecondary }}
                >
                  <Refresh sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            {data.run_url && (
              <Tooltip title="View on GitHub">
                <IconButton
                  size="small"
                  href={data.run_url}
                  target="_blank"
                  rel="noopener"
                  component="a"
                  sx={{ color: COLORS.textSecondary }}
                >
                  <OpenInNew sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Status Chip + Progress */}
        <Box sx={{ px: 2.5, pt: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Chip
              icon={getStatusIcon(data.status || '', data.conclusion)}
              label={getStatusLabel(isComplete, isRunning, data.conclusion)}
              size="small"
              sx={{
                bgcolor: `${getStatusColor(data.status || '', data.conclusion)}20`,
                color: getStatusColor(data.status || '', data.conclusion),
                fontWeight: 600,
                fontSize: '0.78rem',
                '& .MuiChip-icon': { ml: 0.5 },
              }}
            />
            <Typography
              sx={{ color: COLORS.textSecondary, fontSize: '0.75rem' }}
            >
              {data.started_at && formatDuration(data.started_at, data.updated_at)}
            </Typography>
          </Box>

          {(isRunning || data.status === 'queued') && (
            <LinearProgress
              variant={isRunning ? 'determinate' : 'indeterminate'}
              value={progressPercent}
              sx={{
                mb: 1.5,
                borderRadius: 4,
                height: 6,
                bgcolor: COLORS.bgStep,
                '& .MuiLinearProgress-bar': {
                  bgcolor: COLORS.primary,
                  borderRadius: 4,
                },
              }}
            />
          )}
        </Box>

        {/* Jobs */}
        <Box sx={{ px: 2.5, pb: 2 }}>
          {data.jobs?.map(job => {
            const isExpanded = expandedJobs.has(job.name);
            const jobColor = getStatusColor(job.status, job.conclusion);

            return (
              <Box
                key={job.name}
                sx={{
                  mb: 1,
                  borderRadius: '10px',
                  border: `1px solid ${jobColor}30`,
                  bgcolor: COLORS.bgCard,
                  overflow: 'hidden',
                }}
              >
                {/* Job Header */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: COLORS.bgStep },
                    transition: 'background 0.2s',
                  }}
                  onClick={() => toggleJob(job.name)}
                >
                  {getStatusIcon(job.status, job.conclusion)}
                  <Typography
                    sx={{
                      flex: 1,
                      color: COLORS.textPrimary,
                      fontSize: '0.85rem',
                      fontWeight: 500,
                    }}
                  >
                    {job.name}
                  </Typography>
                  <Typography
                    sx={{
                      color: COLORS.textSecondary,
                      fontSize: '0.72rem',
                      mr: 0.5,
                    }}
                  >
                    {job.started_at &&
                      formatDuration(job.started_at, job.completed_at)}
                  </Typography>
                  {isExpanded ? (
                    <ExpandLess
                      sx={{ color: COLORS.textSecondary, fontSize: 18 }}
                    />
                  ) : (
                    <ExpandMore
                      sx={{ color: COLORS.textSecondary, fontSize: 18 }}
                    />
                  )}
                </Box>

                {/* Steps */}
                <Collapse in={isExpanded}>
                  <Box sx={{ px: 1.5, pb: 1.5 }}>
                    {job.steps?.map(step => {
                      const stepColor = getStatusColor(
                        step.status,
                        step.conclusion,
                      );
                      return (
                        <Box
                          key={step.number}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            py: 0.5,
                            pl: 1,
                            borderLeft: `2px solid ${stepColor}40`,
                          }}
                        >
                          {getStatusIcon(step.status, step.conclusion)}
                          <Typography
                            sx={{
                              flex: 1,
                              color:
                                step.conclusion === 'failure'
                                  ? COLORS.error
                                  : COLORS.textSecondary,
                              fontSize: '0.78rem',
                              fontWeight:
                                step.conclusion === 'failure' ? 600 : 400,
                            }}
                          >
                            {step.name}
                          </Typography>
                          <Typography
                            sx={{
                              color: COLORS.textSecondary,
                              fontSize: '0.68rem',
                            }}
                          >
                            {step.started_at &&
                              formatDuration(
                                step.started_at,
                                step.completed_at,
                              )}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>

        {/* Error Messages */}
        {data.error_messages && data.error_messages.length > 0 && (
          <Box
            sx={{
              mx: 2.5,
              mb: 2,
              p: 1.5,
              borderRadius: '10px',
              bgcolor: 'rgba(244,67,54,0.08)',
              border: `1px solid ${COLORS.error}30`,
            }}
          >
            <Typography
              sx={{
                color: COLORS.error,
                fontSize: '0.78rem',
                fontWeight: 600,
                mb: 0.5,
              }}
            >
              ⚠️ Errors
            </Typography>
            {data.error_messages.slice(0, 3).map((msg, i) => (
              <Typography
                key={i}
                sx={{
                  color: COLORS.textSecondary,
                  fontSize: '0.75rem',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  mt: 0.5,
                }}
              >
                {msg}
              </Typography>
            ))}
          </Box>
        )}
      </Paper>
    </Fade>
  );
};

export default DeploymentTracker;
